import React from 'react';
import { 
  Card, 
  Timeline, 
  Space, 
  Tag, 
  Button, 
  List, 
  Popover,
  Typography
} from 'antd';
import { 
  ScheduleOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { PlanogramTemplate, MilestoneConfig } from '../../../../../types/plan-template';

const { Text } = Typography;

interface MilestoneVisualizationProps {
  template: PlanogramTemplate;
}

// 计算考虑并行和串行逻辑的累计时间
const calculateCumulativeDays = (milestones: MilestoneConfig[], currentIndex: number): number => {
  let totalDays = 0;
  let parallelGroup: MilestoneConfig[] = [];
  
  for (let i = 0; i < currentIndex; i++) {
    const milestone = milestones[i];
    
    if (milestone.parallelExecution) {
      // 如果是并行的，加入当前并行组
      parallelGroup.push(milestone);
    } else {
      // 如果是串行的，先处理之前的并行组
      if (parallelGroup.length > 0) {
        // 取并行组中耗时最大的
        const maxDuration = Math.max(
          ...parallelGroup.map(m => m.durationEstimate + m.bufferTime)
        );
        totalDays += maxDuration;
        // 清空并行组
        parallelGroup = [];
      }
      // 加上当前串行里程碑的耗时
      totalDays += milestone.durationEstimate + milestone.bufferTime;
    }
  }
  
  // 处理最后可能剩余的并行组
  if (parallelGroup.length > 0) {
    const maxDuration = Math.max(
      ...parallelGroup.map(m => m.durationEstimate + m.bufferTime)
    );
    totalDays += maxDuration;
  }
  
  return totalDays;
};

// 计算总耗时
const calculateTotalDuration = (milestones: MilestoneConfig[]): number => {
  if (milestones.length === 0) return 0;
  
  let totalDays = 0;
  let parallelGroup: MilestoneConfig[] = [];
  
  for (let i = 0; i < milestones.length; i++) {
    const milestone = milestones[i];
    
    if (milestone.parallelExecution) {
      // 如果是并行的，加入当前并行组
      parallelGroup.push(milestone);
    } else {
      // 如果是串行的，先处理之前的并行组
      if (parallelGroup.length > 0) {
        // 取并行组中耗时最大的
        const maxDuration = Math.max(
          ...parallelGroup.map(m => m.durationEstimate + m.bufferTime)
        );
        totalDays += maxDuration;
        // 清空并行组
        parallelGroup = [];
      }
      // 加上当前串行里程碑的耗时
      totalDays += milestone.durationEstimate + milestone.bufferTime;
    }
  }
  
  // 处理最后可能剩余的并行组
  if (parallelGroup.length > 0) {
    const maxDuration = Math.max(
      ...parallelGroup.map(m => m.durationEstimate + m.bufferTime)
    );
    totalDays += maxDuration;
  }
  
  return totalDays;
};

const MilestoneVisualization: React.FC<MilestoneVisualizationProps> = ({
  template
}) => {
  const { t } = useTranslation();
  const sortedMilestones = [...template.milestones].sort((a, b) => a.sequence - b.sequence);
  const totalDuration = calculateTotalDuration(sortedMilestones);

  return (
    <Card 
      title={
        <Space>
          <ScheduleOutlined />
          {t('planTemplate.detail.milestoneVisualization.title')}
        </Space>
      }
      extra={
        <Space>
          <Text>{t('planTemplate.detail.milestoneVisualization.totalMilestones', { count: sortedMilestones.length })}</Text>
          <Text>{t('planTemplate.detail.milestoneVisualization.estimatedDuration', { days: totalDuration })}</Text>
        </Space>
      }
    >
      <Timeline>
        {sortedMilestones.map((milestone, index) => {
          const cumulativeDays = calculateCumulativeDays(sortedMilestones, index);
          
          return (
            <Timeline.Item
              key={milestone.id}
              color={milestone.isFinalMilestone ? 'red' : 'blue'}
              dot={milestone.isFinalMilestone ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
            >
              <div>
                <Space>
                  <Text strong>{milestone.name}</Text>
                  {/* <Tag>{t('planTemplate.detail.milestoneVisualization.dayRange', { 
                    start: cumulativeDays + 1, 
                    end: cumulativeDays + milestone.durationEstimate + milestone.bufferTime 
                  })}</Tag> */}
                </Space>
                <br />
                <Text type="secondary">{milestone.description}</Text>
                <br />
                <Space size="small" style={{ marginTop: 4 }}>
                  <Tag color="blue">{milestone.responsibleTeam}</Tag>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {t('planTemplate.detail.milestoneVisualization.duration', {
                      duration: milestone.durationEstimate,
                      buffer: milestone.bufferTime
                    })}
                  </Text>
                  {milestone.parallelExecution && (
                    <Tag color="orange">{t('planTemplate.detail.milestoneVisualization.parallelExecution')}</Tag>
                  )}
                  {milestone.deliverables && milestone.deliverables.length > 0 && (
                    <Popover
                      content={
                        <List
                          size="small"
                          dataSource={milestone.deliverables}
                          renderItem={item => <List.Item>{item}</List.Item>}
                        />
                      }
                      title={t('planTemplate.detail.milestoneVisualization.deliverables')}
                    >
                      <Button type="link" size="small" icon={<EyeOutlined />}>
                        {t('planTemplate.detail.milestoneVisualization.viewDeliverables', { count: milestone.deliverables.length })}
                      </Button>
                    </Popover>
                  )}
                </Space>
              </div>
            </Timeline.Item>
          );
        })}
      </Timeline>
    </Card>
  );
};

export default MilestoneVisualization;